<script setup lang="ts">
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useCartStore } from '../../stores/cart';
import { useClientAuthStore } from '../../stores/auth';
import { useAppMetaStore } from '../../stores/appMeta';
import { usePwaChatStore } from '../../stores/chat';

const route = useRoute();
const router = useRouter();
const cart = useCartStore();
const auth = useClientAuthStore();
const meta = useAppMetaStore();
const chat = usePwaChatStore();

// D-CHAT-15: a aba só existe para cliente logado + canal Web habilitado no
// tenant — sem isso, a nav mantém os 4 itens originais (sem slot vazio).
const showMessages = computed(() => Boolean(auth.token) && meta.webChatEnabled);

function active(path: string): boolean {
    return route.path === path || route.path.startsWith(path + '/');
}
</script>

<template>
    <nav
        class="fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-gray-100"
        style="padding-bottom: env(safe-area-inset-bottom, 0px);"
    >
        <div :class="showMessages ? 'grid grid-cols-5' : 'grid grid-cols-4'">

            <!-- Catálogo -->
            <button
                type="button"
                class="flex flex-col items-center justify-center gap-[3px] py-3 border-0 bg-transparent cursor-pointer"
                :class="active('/') ? 'text-[var(--primary)]' : 'text-[var(--muted)]'"
                @click="router.push('/')"
            >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                    <path d="M4 11.5 12 4l8 7.5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    <path
                        d="M6 10v9a1 1 0 0 0 1 1h3v-5.5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1V20h3a1 1 0 0 0 1-1v-9"
                        stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                    />
                </svg>
                <span class="text-[11px] font-semibold">Catálogo</span>
            </button>

            <!-- Pedidos -->
            <button
                type="button"
                class="flex flex-col items-center justify-center gap-[3px] py-3 border-0 bg-transparent cursor-pointer"
                :class="active('/meus-pedidos') ? 'text-[var(--primary)]' : 'text-[var(--muted)]'"
                @click="router.push('/meus-pedidos')"
            >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                    <path
                        d="M6 3h12a1 1 0 0 1 1 1v16l-2.5-1.5L14 20l-2.5-1.5L9 20l-2.5-1.5L4 20V4a1 1 0 0 1 1-1Z"
                        stroke-width="2" stroke-linejoin="round"
                    />
                    <path d="M8 8h8M8 12h8M8 16h4" stroke-width="1.6" stroke-linecap="round" />
                </svg>
                <span class="text-[11px] font-semibold">Pedidos</span>
            </button>

            <!-- Mensagens (D-CHAT-15) -->
            <button
                v-if="showMessages"
                type="button"
                class="flex flex-col items-center justify-center gap-[3px] py-3 relative border-0 bg-transparent cursor-pointer"
                :class="active('/mensagens') ? 'text-[var(--primary)]' : 'text-[var(--muted)]'"
                @click="router.push('/mensagens')"
            >
                <div class="relative">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                        <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                    <span
                        v-if="chat.unreadCount > 0"
                        class="absolute -top-1.5 -right-2 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-extrabold text-white grid place-items-center border-2 border-white"
                        style="background: var(--primary);"
                    >
                        {{ chat.unreadCount > 99 ? '99+' : chat.unreadCount }}
                    </span>
                </div>
                <span class="text-[11px] font-semibold">Mensagens</span>
            </button>

            <!-- Carrinho -->
            <button
                type="button"
                class="flex flex-col items-center justify-center gap-[3px] py-3 relative border-0 bg-transparent cursor-pointer"
                :class="active('/carrinho') ? 'text-[var(--primary)]' : 'text-[var(--muted)]'"
                @click="router.push('/carrinho')"
            >
                <div class="relative">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                        <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                        <path d="M3 6h18" stroke-width="2" stroke-linecap="round" />
                        <path d="M16 10a4 4 0 0 1-8 0" stroke-width="2" stroke-linecap="round" />
                    </svg>
                    <span
                        v-if="cart.count > 0"
                        class="absolute -top-1.5 -right-2 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-extrabold text-white grid place-items-center border-2 border-white"
                        style="background: var(--primary);"
                    >
                        {{ cart.count > 99 ? '99+' : cart.count }}
                    </span>
                </div>
                <span class="text-[11px] font-semibold">Carrinho</span>
            </button>

            <!-- Perfil -->
            <button
                type="button"
                class="flex flex-col items-center justify-center gap-[3px] py-3 border-0 bg-transparent cursor-pointer"
                :class="active('/perfil') ? 'text-[var(--primary)]' : 'text-[var(--muted)]'"
                @click="router.push('/perfil')"
            >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                    <circle cx="12" cy="8" r="3.5" stroke-width="2" />
                    <path d="M4 20c0-3.866 3.582-7 8-7s8 3.134 8 7" stroke-width="2" stroke-linecap="round" />
                </svg>
                <span class="text-[11px] font-semibold whitespace-nowrap">Minha conta</span>
            </button>

        </div>
    </nav>
</template>
